/**
 * Optional bridge: mirror v1 start/stop into legacy NekoHR timer log rows (nekoapi).
 */
import axios from "@/services/axios.config";
import {
  GetCurrentTimeUtc,
  hoursDecimalForLegacyApi,
  hoursDecimalToWholeSeconds,
  today,
  UpdateCurrentTimerData,
} from "@/Utilites/timerHelper";
import {resolveTimerLogUserId} from "@/services/timerActor";

const createUrl = "api/users/timers/create-new-timer/";
const updateUrl = "api/users/timers/update-timer-by-id/";

function legacyStopPayload(legacyLogId, elapsedHours, legacyRow, actorUserId) {
  const h = hoursDecimalForLegacyApi(Number(elapsedHours) || 0);
  const endTime = GetCurrentTimeUtc();
  const userId = resolveTimerLogUserId(legacyRow, {user_id: actorUserId});
  const payload = {
    id: legacyLogId,
    hours: h,
    hours_with_timer: h,
    working_hours: h,
    timer_started_at: null,
    timer_stopped_at: endTime,
  };
  if (userId != null) payload.user_id = userId;
  if (legacyRow && typeof legacyRow === "object") {
    const logDate = legacyRow.timer_log_date || legacyRow.log_date;
    if (logDate) {
      payload.timer_log_date = logDate;
      payload.log_date = logDate;
    }
    if (legacyRow.project_id != null) payload.project_id = legacyRow.project_id;
    if (legacyRow.task_description) {
      payload.task_description = legacyRow.task_description;
      payload.log_description =
        legacyRow.log_description || legacyRow.task_description;
    }
  }
  return payload;
}

export async function patchLegacyRunningLog(
  legacyLog,
  {startedAt, projectId, taskDescription, baseHours}
) {
  if (!legacyLog || legacyLog.id == null) return null;
  const started = startedAt || GetCurrentTimeUtc();
  const desc =
    taskDescription != null
      ? String(taskDescription || "").trim() ||
        "<Please Enter a Description Here>"
      : legacyLog.task_description;
  const h =
    baseHours != null ? Number(baseHours) : Number(legacyLog.hours) || 0;
  const data = {
    ...legacyLog,
    user_id: resolveTimerLogUserId(legacyLog, null) ?? legacyLog.user_id,
    timer_started_at: started,
    timer_initiated_at: started,
    timer_stopped_at: null,
    hours: h,
    hours_with_timer: h,
    working_hours: h,
    project_id:
      projectId != null && projectId !== "" ? projectId : legacyLog.project_id,
    task_description: desc,
    log_description: desc,
  };
  const response = await axios.patch(`${updateUrl}${data.id}`, {...data});
  return response.data;
}

export async function createLegacyRunningLog({
  userId,
  projectId,
  taskDescription,
  startedAt,
  timerLogDate,
}) {
  const uid = userId != null ? Number(userId) : null;
  if (!Number.isFinite(uid) || uid <= 0) {
    throw new Error("createLegacyRunningLog: missing user_id");
  }
  const started = startedAt || GetCurrentTimeUtc();
  const logDate = timerLogDate || today();
  const desc =
    String(taskDescription || "").trim() || "<Please Enter a Description Here>";
  const form = {
    user_id: uid,
    hours_with_timer: 0,
    hours: 0,
    working_hours: 0,
    is_submitted: true,
    timer_started_at: started,
    timer_initiated_at: started,
    timer_stopped_at: null,
    timer_log_date: logDate,
    log_date: logDate,
    project_id: projectId != null ? projectId : null,
    task_description: desc,
    log_description: desc,
  };
  const response = await axios.post(createUrl, form);
  return response.data;
}

export async function stopLegacyTimerLog({
  legacyLog,
  displayTimeHms,
  stoppedAt,
}) {
  if (!legacyLog || legacyLog.id == null) return null;
  const data = {
    ...legacyLog,
    user_id: resolveTimerLogUserId(legacyLog, null) ?? legacyLog.user_id,
  };
  if (displayTimeHms) {
    UpdateCurrentTimerData(data, displayTimeHms);
  } else if (stoppedAt) {
    data.timer_stopped_at = stoppedAt;
    data.timer_started_at = null;
  }
  const response = await axios.patch(`${updateUrl}${data.id}`, {...data});
  return response.data;
}

export async function stopLegacyTimerLogWithHours({legacyLog, elapsedHours}) {
  if (!legacyLog || legacyLog.id == null) return null;
  const secs = hoursDecimalToWholeSeconds(Number(elapsedHours) || 0);
  const hh = Math.floor(secs / 3600);
  const mm = Math.floor((secs % 3600) / 60);
  const ss = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const hms = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
  return stopLegacyTimerLog({legacyLog, displayTimeHms: hms});
}

/**
 * Stop a legacy log by id; always sends user_id + log date so PATCH cannot reassign owner.
 */
export async function stopLegacyTimerLogById(
  legacyLogId,
  elapsedHours,
  legacyRow = null,
  actorUserId = null
) {
  if (legacyLogId == null || legacyLogId === "") return null;
  const payload = legacyStopPayload(
    legacyLogId,
    elapsedHours,
    legacyRow,
    actorUserId
  );
  const response = await axios.patch(`${updateUrl}${legacyLogId}`, payload);
  return response.data;
}

export function hoursFromV1Session(session) {
  if (!session) return 0;
  if (session.elapsed_hours != null) return Number(session.elapsed_hours);
  if (session.elapsed_seconds != null) {
    return Number(session.elapsed_seconds) / 3600;
  }
  return 0;
}
